import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  BookOpen,
  UserCircle,
  Barcode,
  Plus,
  X,
  Download,
  Trash2,
  Check,
  Clock,
  XCircle,
  FileSpreadsheet,
  Camera,
  Loader2,
  ScanLine
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format date as mm/dd/yy
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day}/${year} ${hours}:${mins}`;
  } catch {
    return dateStr;
  }
};

export const OfflineCheckout = ({ user }) => {
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("new");
  
  // New checkout form
  const [memberBarcode, setMemberBarcode] = useState("");
  const [memberName, setMemberName] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [currentBookBarcode, setCurrentBookBarcode] = useState("");
  const [currentBookTitle, setCurrentBookTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [scanTarget, setScanTarget] = useState(null); // 'member' or 'book'
  const html5QrCodeRef = useRef(null);
  const scannerRef = useRef(null);
  
  const bookBarcodeRef = useRef(null);

  // Supported barcode formats
  const SUPPORTED_BARCODE_FORMATS = [
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODABAR,
    Html5QrcodeSupportedFormats.ITF,
  ];

  // Clean Codabar start/stop characters
  const cleanBarcodeValue = (barcode) => {
    if (!barcode) return barcode;
    return barcode.replace(/^[ABCDabcd]/, '').replace(/[ABCDabcd]$/, '');
  };

  useEffect(() => {
    fetchCheckouts();
  }, []);

  // Scanner functions
  const startScanner = async (target) => {
    setScanTarget(target);
    setScannerOpen(true);
    setScannerReady(false);
    
    setTimeout(async () => {
      try {
        const element = document.getElementById("desktop-checkout-barcode-reader");
        if (!element) {
          console.error("Scanner element not found");
          toast.error("Scanner element not ready");
          return;
        }
        
        html5QrCodeRef.current = new Html5Qrcode("desktop-checkout-barcode-reader", {
          formatsToSupport: SUPPORTED_BARCODE_FORMATS,
          verbose: false
        });
        
        // Get container width for responsive qrbox
        const containerWidth = element.offsetWidth || 400;
        const qrboxWidth = Math.min(containerWidth - 40, 320);
        const qrboxHeight = Math.min(120, qrboxWidth * 0.4);
        
        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          {
            fps: 15,  // Higher FPS for faster detection
            qrbox: { width: qrboxWidth, height: qrboxHeight },
            aspectRatio: 1.0,  // Square aspect ratio works better
            disableFlip: false,
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true  // Use native BarcodeDetector API if available
            }
          },
          (decodedText, decodedResult) => {
            const cleanedBarcode = cleanBarcodeValue(decodedText);
            console.log("Scanned:", decodedText, "Cleaned:", cleanedBarcode, "Format:", decodedResult?.result?.format?.formatName);
            
            if (target === 'member') {
              setMemberBarcode(cleanedBarcode);
              toast.success(`Member barcode: ${cleanedBarcode}`);
            } else if (target === 'book') {
              setCurrentBookBarcode(cleanedBarcode);
              toast.success(`Book barcode: ${cleanedBarcode}`);
            }
            
            stopScanner();
          },
          (errorMessage) => {
            // Ignore scan errors
          }
        );
        setScannerReady(true);
      } catch (err) {
        console.error("Failed to start scanner:", err);
        toast.error("Could not access camera");
        setScannerOpen(false);
      }
    }, 300);  // Increased delay for DOM to be ready
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore stop errors
      }
      html5QrCodeRef.current = null;
    }
    setScannerOpen(false);
    setScannerReady(false);
    setScanTarget(null);
  };

  const fetchCheckouts = async () => {
    try {
      const response = await axios.get(`${API}/offline-checkout/list`);
      setCheckouts(response.data);
    } catch (error) {
      toast.error("Failed to fetch checkouts");
    } finally {
      setLoading(false);
    }
  };

  const addBookItem = () => {
    if (!currentBookBarcode.trim()) {
      toast.error("Please enter a book barcode");
      return;
    }
    
    // Check for duplicates
    if (items.some(item => item.book_barcode === currentBookBarcode.trim())) {
      toast.error("This book is already added");
      return;
    }
    
    setItems([...items, {
      book_barcode: currentBookBarcode.trim(),
      book_title: currentBookTitle.trim() || null
    }]);
    
    setCurrentBookBarcode("");
    setCurrentBookTitle("");
    
    // Focus back to barcode input
    bookBarcodeRef.current?.focus();
  };

  const removeBookItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmitCheckout = async (downloadCSV = false) => {
    if (!memberBarcode.trim()) {
      toast.error("Please enter member barcode");
      return;
    }
    if (items.length === 0) {
      toast.error("Please add at least one book");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (downloadCSV) {
        // Download CSV directly
        const response = await axios.post(
          `${API}/offline-checkout/batch/csv`,
          {
            member_barcode: memberBarcode.trim(),
            member_name: memberName.trim() || null,
            items: items,
            notes: notes.trim() || null
          },
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `checkout_${memberBarcode}_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success("CSV downloaded and checkout saved");
      } else {
        // Just save to database
        await axios.post(`${API}/offline-checkout/create`, {
          member_barcode: memberBarcode.trim(),
          member_name: memberName.trim() || null,
          items: items,
          notes: notes.trim() || null
        });
        
        toast.success("Checkout saved successfully");
      }
      
      // Reset form
      setMemberBarcode("");
      setMemberName("");
      setNotes("");
      setItems([]);
      
      // Refresh list
      fetchCheckouts();
      
    } catch (error) {
      toast.error("Failed to process checkout");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportAll = async (status = "pending") => {
    try {
      const response = await axios.get(
        `${API}/offline-checkout/export/csv?status=${status}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `offline_checkouts_${status}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleUpdateStatus = async (checkoutId, status) => {
    try {
      await axios.put(`${API}/offline-checkout/${checkoutId}/status?status=${status}`);
      toast.success(`Status updated to ${status}`);
      fetchCheckouts();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (checkoutId) => {
    if (!confirm("Are you sure you want to delete this checkout record?")) return;
    
    try {
      await axios.delete(`${API}/offline-checkout/${checkoutId}`);
      toast.success("Checkout deleted");
      fetchCheckouts();
    } catch (error) {
      toast.error("Failed to delete checkout");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "synced":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><Check className="h-3 w-3 mr-1" />Synced</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = checkouts.filter(c => c.status === "pending").length;
  const syncedCount = checkouts.filter(c => c.status === "synced").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading flex items-center gap-2">
            <Barcode className="h-8 w-8 text-blue-600" />
            Offline Checkout
          </h1>
          <p className="text-muted-foreground">Record checkouts for later sync with member and book barcodes</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1">
            <Clock className="h-3 w-3 mr-1 text-amber-500" />
            {pendingCount} pending
          </Badge>
          <Badge variant="outline" className="px-3 py-1">
            <Check className="h-3 w-3 mr-1 text-green-500" />
            {syncedCount} synced
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="new">New Checkout</TabsTrigger>
          <TabsTrigger value="history">History ({checkouts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Member & Book Entry */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCircle className="h-5 w-5 text-blue-600" />
                  Member Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Member Barcode *</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Scan or enter member barcode"
                      value={memberBarcode}
                      onChange={(e) => setMemberBarcode(e.target.value)}
                      className="font-mono"
                      data-testid="member-barcode-input"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => startScanner('member')}
                      data-testid="scan-member-barcode-btn"
                    >
                      <Camera className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Member Name (optional)</Label>
                  <Input
                    placeholder="Enter member name"
                    value={memberName}
                    onChange={(e) => setMemberName(e.target.value)}
                    data-testid="member-name-input"
                  />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    placeholder="Any additional notes..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Book Items */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-purple-600" />
                  Books to Checkout
                </CardTitle>
                <CardDescription>Add books by scanning or entering barcodes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      ref={bookBarcodeRef}
                      placeholder="Book barcode"
                      value={currentBookBarcode}
                      onChange={(e) => setCurrentBookBarcode(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addBookItem()}
                      className="font-mono"
                      data-testid="book-barcode-input"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => startScanner('book')}
                    data-testid="scan-book-barcode-btn"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                  <div className="flex-1">
                    <Input
                      placeholder="Title (optional)"
                      value={currentBookTitle}
                      onChange={(e) => setCurrentBookTitle(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addBookItem()}
                    />
                  </div>
                  <Button onClick={addBookItem} data-testid="add-book-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No books added yet</p>
                    <p className="text-sm">Scan or enter book barcodes above</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {items.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-purple-50 border border-purple-200"
                      >
                        <div className="flex items-center gap-3">
                          <Barcode className="h-4 w-4 text-purple-500" />
                          <div>
                            <p className="font-mono text-sm font-medium">{item.book_barcode}</p>
                            {item.book_title && (
                              <p className="text-xs text-muted-foreground">{item.book_title}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBookItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {items.length > 0 && (
                  <div className="text-sm text-muted-foreground text-right">
                    {items.length} book{items.length !== 1 ? "s" : ""} added
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => handleSubmitCheckout(false)}
              disabled={isSubmitting || !memberBarcode || items.length === 0}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save Checkout
            </Button>
            <Button
              onClick={() => handleSubmitCheckout(true)}
              disabled={isSubmitting || !memberBarcode || items.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-csv-btn"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Save & Download CSV
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Checkout History</CardTitle>
                <CardDescription>All offline checkout records</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExportAll("pending")}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export Pending
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExportAll("")}>
                  <Download className="h-4 w-4 mr-2" />
                  Export All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : checkouts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Barcode className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No checkout records yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Books</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {checkouts.map((checkout) => (
                      <TableRow key={checkout.id}>
                        <TableCell className="font-mono text-sm">
                          {formatDate(checkout.checkout_date)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-mono text-sm">{checkout.member_barcode}</p>
                            {checkout.member_name && (
                              <p className="text-xs text-muted-foreground">{checkout.member_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {checkout.items?.length || 0} book{(checkout.items?.length || 0) !== 1 ? "s" : ""}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {checkout.created_by_name}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(checkout.status)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {checkout.status === "pending" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleUpdateStatus(checkout.id, "synced")}
                                title="Mark as synced"
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(checkout.id)}
                              title="Delete"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Barcode Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={(open) => !open && stopScanner()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" />
              Scan {scanTarget === 'member' ? 'Member' : 'Book'} Barcode
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Center the barcode in the box - it will be detected automatically
            </p>
            
            <div 
              id="desktop-checkout-barcode-reader" 
              ref={scannerRef}
              className="w-full aspect-square bg-black rounded-lg overflow-hidden"
              style={{ minHeight: '320px' }}
            />
            
            {!scannerReady && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-sm">Starting camera...</p>
              </div>
            )}
            
            {scannerReady && (
              <p className="text-xs text-center text-emerald-600 font-medium">
                Camera active - scanning for barcodes...
              </p>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  const manual = prompt(`Enter ${scanTarget === 'member' ? 'member' : 'book'} barcode manually:`);
                  if (manual) {
                    if (scanTarget === 'member') {
                      setMemberBarcode(manual.trim());
                    } else {
                      setCurrentBookBarcode(manual.trim());
                    }
                    stopScanner();
                    toast.success("Barcode entered");
                  }
                }}
              >
                Enter Manually
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={stopScanner}
              >
                Cancel
              </Button>
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              The barcode will be detected automatically when visible. Supports: ISBN, UPC, Code 39, Code 128, Codabar
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfflineCheckout;
